import React from 'react';

/**
 * Common Reusable Table Skeleton Loader Component
 * Renders a shimmering, pulsating skeleton loader matching table columns and rows.
 */
const TableSkeleton = ({
    columns = [],
    colCount = 5,
    rows = 5,
    cardWrapper = true,
    className = ""
}) => {
    // Generate normalized column configurations
    const effectiveCols = columns.length > 0
        ? columns
        : Array.from({ length: colCount }).map((_, idx) => ({
            label: '',
            span: idx === 0 ? 'col-span-3' : idx === colCount - 1 ? 'col-span-2' : 'col-span-2',
            align: idx === colCount - 1 ? 'right' : 'left'
        }));

    const content = (
        <div className={`w-full overflow-hidden select-none ${className}`}>
            {/* Header skeleton */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50/70 border-b border-slate-100 items-center">
                {effectiveCols.map((col, idx) => (
                    <div
                        key={idx}
                        className={`${col.span || 'col-span-2'} flex ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : 'justify-start'}`}
                    >
                        {col.label ? (
                            <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                {col.label}
                            </span>
                        ) : (
                            <div className="h-3 bg-slate-200/70 rounded-md w-20 animate-pulse" />
                        )}
                    </div>
                ))}
            </div>

            {/* Rows skeleton */}
            <div className="divide-y divide-slate-100">
                {Array.from({ length: rows }).map((_, rowIdx) => (
                    <div
                        key={rowIdx}
                        className="hidden md:grid grid-cols-12 gap-4 items-center px-6 py-4 bg-white hover:bg-slate-50/50 transition-colors"
                    >
                        {effectiveCols.map((col, colIdx) => (
                            <div
                                key={colIdx}
                                className={`${col.span || 'col-span-2'} flex ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : 'justify-start'} items-center`}
                            >
                                {colIdx === 0 ? (
                                    /* Primary entity cell (Avatar/Icon + 2 lines of text) */
                                    <div className="flex items-center gap-3 w-full">
                                        <div className="w-8 h-8 rounded-full bg-slate-100 shrink-0 animate-pulse" />
                                        <div className="flex-1 min-w-0 space-y-1.5">
                                            <div className="h-3.5 bg-slate-200/80 rounded-md w-3/4 animate-pulse" />
                                            <div className="h-2.5 bg-slate-100 rounded w-1/2 animate-pulse" />
                                        </div>
                                    </div>
                                ) : colIdx === effectiveCols.length - 1 ? (
                                    /* Action buttons cell */
                                    <div className="flex items-center gap-2">
                                        <div className="h-7 w-20 bg-slate-100 rounded-xl animate-pulse" />
                                    </div>
                                ) : colIdx % 2 === 0 ? (
                                    /* Status badge skeleton */
                                    <div className="h-5 w-20 bg-slate-100 rounded-full animate-pulse" />
                                ) : (
                                    /* Normal text cell */
                                    <div className="space-y-1 w-full max-w-[140px]">
                                        <div className="h-3.5 bg-slate-100 rounded-md w-4/5 animate-pulse" />
                                        <div className="h-2.5 bg-slate-100/60 rounded w-1/2 animate-pulse" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* Mobile skeleton cards */}
            <div className="md:hidden p-3.5 space-y-3">
                {Array.from({ length: Math.min(3, rows) }).map((_, idx) => (
                    <div key={idx} className="p-4 bg-white border border-slate-100 rounded-2xl space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-slate-100 animate-pulse" />
                                <div className="space-y-1.5">
                                    <div className="h-3.5 bg-slate-200/80 rounded w-28 animate-pulse" />
                                    <div className="h-2.5 bg-slate-100 rounded w-16 animate-pulse" />
                                </div>
                            </div>
                            <div className="h-5 w-16 bg-slate-100 rounded-full animate-pulse" />
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-50">
                            <div className="h-3 bg-slate-100 rounded w-24 animate-pulse" />
                            <div className="h-3 bg-slate-100 rounded w-16 animate-pulse" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    if (!cardWrapper) {
        return content;
    }

    return (
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-slate-100 overflow-hidden relative">
            {content}
        </div>
    );
};

export default TableSkeleton;
